import React, { useState, useEffect } from 'react';
import { People, PersonAdd } from '@mui/icons-material';
import AdminSidebar from '../../components/admin/AdminSidebar';
import DashboardHeader from '../../components/admin/DashboardHeader';
import {RolesStats} from "./RolesPermissions/RolesStats";
import {RolesFilters} from "./RolesPermissions/RolesFilters";
import {MemberCard} from "./RolesPermissions/MemberCard";
import {InviteUserModal} from "./RolesPermissions/InviteUserModal";
import {MemberDetailsModal} from "./RolesPermissions/MemberDetailsModal";
import { useAuth } from '../../contexts/AuthContext';
import {
    fetchOrganizationUserRoles,
    mapUserRoleRecord,
    revokeUserRole,
} from '../../services/userRoleService';

const RolesPermissions = () => {
    const { selectedOrganization } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [pge, setPge] = useState(1);
    const [lmt, setLmt] = useState(100);
    const [rol, setRol] = useState('all');
    const [sts, setSts] = useState('all');
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);

    useEffect(() => {
        if (selectedOrganization?.id) {
            loadMembers();
        }
    }, [selectedOrganization, pge, lmt, rol, sts]);

    const loadMembers = async () => {
        if (!selectedOrganization?.id) return;
        
        try {
            setLoading(true);
            const response = await fetchOrganizationUserRoles(selectedOrganization.id, {
                pge,
                lmt,
                sts,
                usr: 'all',
                rol,
            });

            const transformedMembers = (response.contents || [])
                .filter((item) => item.user != null)
                .map((item) => {
                    const mapped = mapUserRoleRecord(item);
                    return {
                        id: item.id,
                        name: mapped.name,
                        phone: mapped.phone || 'N/A',
                        email: mapped.email || `${mapped.phone || ''}@netikash.com`,
                        role: mapped.roleName?.toLowerCase() || mapped.roleId || mapped.role,
                        status: mapped.status,
                        joinDate: item.createdAt || new Date().toISOString(),
                        lastActive: item.updatedAt || new Date().toISOString(),
                        permissions: mapped.permissions,
                        avatar: mapped.role === 'admin' ? '👑' : '💼',
                        rawData: item,
                    };
                });
            
            setMembers(transformedMembers);
            setNextPage(response.nextPage);
            setPrevPage(response.previousPage);
        } catch (error) {
            console.error('Error loading members:', error);
            setMembers([]);
        } finally {
            setLoading(false);
        }
    };

    // Filtrer les membres (filtre côté client pour la recherche)
    const filteredMembers = members.filter(member => {
        const matchesSearch =
            member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.phone.includes(searchTerm);

        // Le filtre de statut est géré par l'API via sts
        return matchesSearch;
    });

    // Calcul des statistiques
    const stats = {
        total: members.length,
        active: members.filter(member => member.status === 'active').length,
        abandoned: members.filter(member => member.status === 'abandoned').length,
        revoked: members.filter(member => member.status === 'revoked').length,
        admins: members.filter(member => member.role === 'admin' && member.status === 'active').length,
        cashiers: members.filter(member => member.role === 'cashier' && member.status === 'active').length
    };

    // Inviter un nouvel utilisateur
    const handleInviteUser = (response) => {
        // Recharger la liste après invitation
        loadMembers();
    };

    // Obtenir les permissions par rôle
    const getPermissionsByRole = (role) => {
        const permissions = {
            'admin': ['Gestion complète', 'Accès admin', 'Modification rôles', 'Supervision équipe'],
            'cashier': ['Gestion transactions', 'Vue caisse', 'Rapports journaliers', 'Scan QR codes']
        };
        return permissions[role] || ['Accès basique'];
    };

    // Voir les détails d'un membre
    const handleViewDetails = (member) => {
        setSelectedMember(member);
        setShowDetailsModal(true);
    };

    // Modifier le rôle d'un membre
    const handleChangeRole = (memberId, newRole) => {
        setMembers(prev => prev.map(member =>
            member.id === memberId
                ? {
                    ...member,
                    role: newRole,
                    permissions: getPermissionsByRole(newRole)
                }
                : member
        ));
        console.log(`Rôle modifié pour ${memberId}: ${newRole}`);
    };

    // Révoquer un membre
    const handleRevokeAccess = async (member) => {
        if (!window.confirm(`Voulez-vous retirer cet utilisateur ?`)) {
            return;
        }
        
        try {
            const response = await revokeUserRole(selectedOrganization.id, member.id);
            if (response) {
                // Recharger la liste
                loadMembers();
                alert('Utilisateur retiré avec succès');
            }
        } catch (error) {
            console.error('Error revoking access:', error);
            alert(error.message || 'Erreur lors de la révocation');
        }
    };

    // Réactiver un membre (modifier le rôle)
    const handleReactivate = (member) => {
        // Ouvrir le modal d'invitation en mode édition
        setSelectedMember(member);
        setShowInviteModal(true);
    };

    return (
        <div className="min-h-screen bg-white flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="users"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-50 via-white to-amber-50 min-h-screen p-4 lg:p-6">
                    <div className="relative z-10 space-y-6 h-full">
                        <DashboardHeader
                            timeRange={timeRange}
                            onTimeRangeChange={setTimeRange}
                            currency={currency}
                            onCurrencyChange={setCurrency}
                            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                        />

                        {/* En-tête de la page */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 text-amber-900 shadow-sm">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-4 mb-4">
                                        <div className="p-2 bg-amber-500/10 rounded-xl">
                                            <People className="text-2xl text-amber-600" />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl font-bold text-amber-900">
                                                Utilisateurs & Rôles
                                            </h1>
                                            <p className="text-amber-700">
                                                Gérez les accès et permissions de votre équipe
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex space-x-3 mt-6 lg:mt-0">
                                    <button
                                        onClick={() => {
                                            setSelectedMember(null);
                                            setShowInviteModal(true);
                                        }}
                                        className="flex items-center space-x-2 px-6 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-all duration-300 font-semibold shadow-md hover:shadow-lg"
                                    >
                                        <PersonAdd />
                                        <span>Inviter un utilisateur</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                <RolesStats stats={stats} />

                <RolesFilters
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    filterStatus={sts}
                    onFilterChange={(status) => {
                        setSts(status);
                        setPge(1); // Reset à la première page
                    }}
                    onInviteUser={() => {
                        setSelectedMember(null);
                        setShowInviteModal(true);
                    }}
                />

                {/* État de chargement */}
                {loading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5, 6].map((skeleton) => (
                            <div key={skeleton} className="bg-white/80 backdrop-blur-lg rounded-3xl p-6 border border-purple-100/50 shadow-lg animate-pulse">
                                <div className="space-y-4">
                                    <div className="h-6 bg-gray-300 rounded w-3/4"></div>
                                    <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                                    <div className="h-8 bg-gray-300 rounded w-full"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Liste des membres */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                            {filteredMembers.map((member) => (
                                <MemberCard
                                    key={member.id}
                                    member={member}
                                    onViewDetails={handleViewDetails}
                                    onChangeRole={handleChangeRole}
                                    onRevokeAccess={handleRevokeAccess}
                                    onReactivate={handleReactivate}
                                />
                            ))}
                        </div>

                        {/* Message si aucun membre trouvé */}
                        {!loading && filteredMembers.length === 0 && (
                            <div className="text-center py-20 bg-white rounded-2xl border border-gray-200 shadow-sm">
                                <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <People className="text-amber-600 text-4xl" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                    Aucun membre trouvé
                                </h3>
                                <p className="text-gray-500 max-w-md mx-auto mb-8 text-lg">
                                    {searchTerm || sts !== 'all'
                                        ? 'Aucun membre ne correspond à vos critères de recherche.'
                                        : 'Invitez vos premiers collaborateurs pour commencer.'
                                    }
                                </p>
                                <button
                                    onClick={() => {
                                        setSelectedMember(null);
                                        setShowInviteModal(true);
                                    }}
                                    className="inline-flex items-center space-x-3 px-10 py-5 bg-amber-500 text-white rounded-2xl font-bold shadow-lg hover:bg-amber-600 transition-all duration-300 transform hover:-translate-y-1"
                                >
                                    <PersonAdd />
                                    <span className="text-lg">Inviter un Utilisateur</span>
                                </button>
                            </div>
                        )}
                    </>
                )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <InviteUserModal
                show={showInviteModal}
                onClose={() => {
                    setShowInviteModal(false);
                    setSelectedMember(null);
                }}
                onInvite={handleInviteUser}
                role={selectedMember?.rawData || null}
            />

            <MemberDetailsModal
                show={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                member={selectedMember}
                onChangeRole={handleChangeRole}
                onRevokeAccess={handleRevokeAccess}
                onReactivate={handleReactivate}
            />
        </div>
    );
};

export default RolesPermissions;